/* Сервис заказа студии без React: состояние, действия и реакции бывшего useStudioForm (use-studio-form.ts), перенесённые
   без изменения поведения. Хук формы — тонкая обёртка (use-order-service.ts): те же запросы в том же порядке, те же ключи
   sessionStorage и тексты. Движок экрана (engine-kit) читает тот же сервис: get() сразу после действия и outcome. */
import { api, endpoints } from '../../../api';
import { clearHistoryRepeat, readHistoryRepeat } from '../../../bridge/history-repeat';
import { ApiError, uuid } from '../../../api/client';
import type { StudioCommand, SpeechOrder } from '../../../api/speech';
import type { UserAvatar } from '../../../api/people';
import type { Voice } from '../../../api/types';
import { UploadRejectedError, UploadTransportError } from '../../../api/uploads';
import { studioApi, studioCheapOffer, studioExpectedPrice, studioFields, type StudioApp, type StudioForm } from '../studio-api';
import { studioPrefill } from '../studio-prefill';
import { studioInputs, studioJsonEnumKeys, studioJsonKeys, validStudioInputs } from '../studio-command';
import { publishStudioOrder } from '../studio-job-bridge';

export interface Intent {
  owner: number;
  key: string;
  body: StudioCommand & { cheap?: boolean };
  orderId?: number;
  /** Поля, которые форма объявила JSON (value_encoding: json): нужны, чтобы проверить тело при восстановлении */
  json?: string[];
}
export const storageKey = (owner: number, appId: number) => `steos:studio:intent:${owner}:${appId}`;
type UploadPolicy = NonNullable<StudioForm['fields'][number]['upload']>;
/** Назначение загрузки: у поля шаблона — purpose, у поля контракта экрана (screen_fields) — список purposes (docs/59 §1).
    Референсы «Фотосессии товара» на стенде приходят только так: reference_1…9 с purposes ["reference"]. */
export function uploadPurpose(policy: UploadPolicy): 'order_input' | 'reference' | 'video_input' {
  const purpose = policy.purpose ?? (policy.purposes?.includes('reference') ? 'reference' : policy.purposes?.includes('video_input') ? 'video_input' : undefined);
  return purpose === 'reference' ? 'reference' : purpose === 'video_input' ? 'video_input' : 'order_input';
}
/** Чем служит референс (`use`), если загрузка его не называет: единственное значение `use` в объекте `reference` контракта экрана. */
export function referenceUse(form: StudioForm): 'style' | 'scene' | 'look' | undefined {
  const reference = studioFields(form).find(field => field.key === 'reference') as { properties?: Array<{ key?: string; values?: unknown }> } | undefined;
  const values = reference?.properties?.find(item => item?.key === 'use')?.values;
  const use = Array.isArray(values) && values.length === 1 ? values[0] : undefined;
  return use === 'style' || use === 'scene' || use === 'look' ? use : undefined;
}
/** Причина неудачной загрузки: «продолжить проверку» имеет смысл только при неизвестном исходе PUT или проверки.
    Отказ сервера (409/4xx на создание слота) и отклонённый файл — окончательные ответы: файл надо выбрать заново. */
export function uploadFailureText(failure: unknown): string {
  if (failure instanceof UploadRejectedError) return 'Файл не прошёл проверку. Выберите другой файл.';
  if (failure instanceof ApiError && failure.status >= 400 && failure.status < 500 && failure.status !== 401 && failure.status !== 408 && failure.status !== 429)
    return failure.code === 'upload_quota_exceeded'
      ? 'Слишком много загрузок подряд. Подождите минуту и выберите файл снова.'
      : 'Файл не принят. Выберите его снова.';
  return 'Загрузка не подтверждена. Продолжите проверку этого файла.';
}
export function restore(owner: number, appId: number): Intent | null {
  const raw = sessionStorage.getItem(storageKey(owner, appId));
  if (!raw) return null;
  const value = JSON.parse(raw) as Intent;
  const body = value?.body;
  if (
    value?.owner !== owner ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.key) ||
    !body ||
    body.app_id !== appId ||
    !Number.isSafeInteger(body.template_version) ||
    body.template_version <= 0 ||
    typeof body.screen !== 'string' ||
    !/^[a-z][a-z0-9_]{0,31}$/.test(body.screen) ||
    !Number.isFinite(body.expected_price) ||
    body.expected_price < 0 ||
    !body.inputs ||
    typeof body.inputs !== 'object' ||
    Array.isArray(body.inputs) ||
    (value.json !== undefined && (!Array.isArray(value.json) || !value.json.every(key => typeof key === 'string' && key.length <= 64))) ||
    !validStudioInputs(body.screen, body.inputs, value.json ?? []) ||
    (value.orderId !== undefined && (!Number.isSafeInteger(value.orderId) || value.orderId <= 0))
  )
    throw new Error('studio_intent_invalid');
  return value;
}
/** В заказ уходят только ключи полей, опубликованных формой (шаблона и контракта экрана, docs/59), и выбор блока «Съёмка»
    (`shoot` проверяет studioInputs): служебные отметки экрана (open_reference и т. п.) в заказ не уходят. */
export function publishedInputs(form: StudioForm, inputs: Record<string, string>) {
  const keys = new Set(studioFields(form).map(field => field.key));
  const shootBlock = ['photo', 'video', 'shoot', 'pshoot'].includes(form.screen);
  return Object.fromEntries(Object.entries(inputs).filter(([key]) => keys.has(key) || (key === 'shoot' && shootBlock)));
}
/** Адрес расчёта цены из формы. Бэкенд публикует полный путь (/bot/v1/app/orders/quote), docs/59 — путь от базы API (/app/orders/quote):
    принимаются оба, но вызывается только путь внутри /app/ (как historyDownloadPath в api/history.ts) */
export function studioQuotePath(raw: unknown): string | null {
  const path = typeof raw === 'string' && raw.startsWith('/bot/v1/') ? raw.slice('/bot/v1'.length) : raw;
  return typeof path === 'string' && /^\/app\/[a-z0-9/_-]+$/i.test(path) ? path : null;
}

/* ---------- контракт сервиса ---------- */

export interface OrderCtx {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  /** Цена под выбранный состав (2 образа, свои фото сцен): экран отдаёт её сам; по умолчанию — studioExpectedPrice */
  expectedPrice?(form: StudioForm, inputs: Record<string, string>): number;
}
/** 17 ячеек useState бывшего хука */
export interface OrderSnapshot {
  identity: { owner: number; appId: number } | null;
  /** Остаток Circle для листа «Не хватает Circle» (balance.circle из GET /app/me); сервер проверяет сам */
  balance: number | null;
  integrityError: string;
  versionStale: boolean;
  intent: Intent | null;
  form: StudioForm | null;
  inputs: Record<string, string>;
  order: SpeechOrder | null;
  busy: boolean;
  /* busy из-за загрузки файла (а не отправки заказа): подвал пишет «Загружаем файл», а не «Создаём» */
  uploadingFile: boolean;
  error: string;
  revision: number;
  uploadIds: Record<string, string>;
  voice: Voice | null;
  rights: boolean;
  result: { url: string; mime: string; name: string; text?: string } | null;
  /* Цена под выбранный состав (docs/59 §5a): форма публикует quote_endpoint — сервер считает сумму по тому же телу заказа. */
  quote: { key: string; amount: number } | null;
}
/** Считается из снимка и контекста при каждом вызове и нигде не хранится */
export interface OrderDerived {
  submissionBlocked: boolean;
  locked: boolean;
  quoteEndpoint: string | null;
  quoteBody: string | null;
  price: number;
  /** price — расчёт сервера (quote) для нынешних входов, а не оценка экрана или цена формы */
  quoted: boolean;
  orderId: number | undefined;
  /** Три состояния (null без заказа) — как в зависимостях опроса бывшего хука */
  terminal: boolean | null;
}
/** Состояние, с которым действие стартует: снимок, производные и контекст. Для хука — рендера, из которого взято действие
    (как замыкание бывшего хука); для движка — текущее состояние сервиса в момент вызова. */
export interface OrderEntry {
  s: OrderSnapshot;
  d: OrderDerived;
  ctx: OrderCtx;
}
export type SubmitOutcome = 'skipped' | 'blocked' | 'created' | 'dropped' | 'kept';
type Next<T> = T | ((current: T) => T);
type Setter<T> = (value: Next<T>) => void;
export type RawPolicy = { kind: 'photo' | 'audio' | 'video' | 'document'; purpose?: string; use?: string };
/** reason — reject_reason окончательного отказа проверки (UploadRejectedError: not_allowed, too_small, unreadable…), если сервер его назвал */
type RawUpload = { id: string } | { error: true; pendingId?: string; reason?: string };
type ResultFile = OrderSnapshot['result'];
/** Слушатель получает новый снимок и только изменившиеся ключи */
export type OrderListener = (snapshot: OrderSnapshot, changed: Partial<OrderSnapshot>) => void;

export interface OrderActions {
  upload(key: string, file: File | null): Promise<boolean | undefined>;
  uploadRaw(policy: RawPolicy, file: File | null, pendingId?: string): Promise<RawUpload>;
  clearUpload(key: string): void;
  importAvatarPhoto(key: string): Promise<void>;
  submit(options?: { cheap?: boolean; price?: number }): Promise<void>;
  refreshStatus(): Promise<void>;
  returnToForm(): Promise<true | undefined>;
  retry(): Promise<boolean>;
  openResult(index: number): Promise<void>;
}
export interface OrderService {
  /** Пропсы последнего зафиксированного рендера useOrderService (рендер хука передаёт свои пропсы явно, как замыкание бывшего хука) */
  ctx: OrderCtx;
  /** Тот же объект до следующего изменяющего set() */
  get(): OrderSnapshot;
  /** По умолчанию — текущий снимок и ctx */
  derive(s?: OrderSnapshot, ctx?: OrderCtx): OrderDerived;
  /** Слушатели вызываются синхронно после каждого изменяющего set().
      since — снимок, который подписчик уже знает: если с тех пор что-то менялось, слушатель сразу получает весь текущий снимок. */
  subscribe(listener: OrderListener, since?: OrderSnapshot): () => void;
  /** Итог последнего submit(); только для движка, в вид хука не входит */
  readonly outcome: SubmitOutcome | null;
  /** Стабильные функции, как setState React */
  readonly setters: {
    inputs: Setter<Record<string, string>>;
    revision: Setter<number>;
    uploadIds: Setter<Record<string, string>>;
    voice: Setter<Voice | null>;
    rights: Setter<boolean>;
  };
  /** Действия на текущем состоянии сервиса (get() и ctx в момент вызова) */
  readonly actions: OrderActions;
  /** Тела эффектов бывшего хука; каждая возвращает свою очистку */
  readonly reactions: {
    owner(appId: number): () => void;
    form(appId: number, avatar: UserAvatar | null, revision: number, initialInputs?: Record<string, unknown>): () => void;
    quote(endpoint: string | null, body: string | null): (() => void) | undefined;
    poll(orderId: number | undefined, terminal: boolean | null): (() => void) | undefined;
    mount(): () => void;
    revoke(result: ResultFile): () => void;
    /** «Попробовать ещё раз»: форма восстановлена — submit на состоянии at (по умолчанию — текущем) */
    retryTick(at?: OrderEntry): void;
  };
}

/** Ровно тот объект, что возвращал бывший хук (CircleFormState = ReturnType<typeof useStudioForm>) */
export type StudioFormView = {
  ownerId: number | undefined;
  balance: number | null;
  price: number;
  uploadRaw: OrderActions['uploadRaw'];
  form: StudioForm | null;
  inputs: Record<string, string>;
  setInputs: Setter<Record<string, string>>;
  intent: Intent | null;
  order: SpeechOrder | null;
  orderId: number | undefined;
  busy: boolean;
  uploadingFile: boolean;
  error: string;
  submissionBlocked: boolean;
  setRevision: Setter<number>;
  locked: boolean;
  rights: boolean;
  setRights: Setter<boolean>;
  uploadIds: Record<string, string>;
  setUploadIds: Setter<Record<string, string>>;
  voice: Voice | null;
  setVoice: Setter<Voice | null>;
  result: ResultFile;
  upload: OrderActions['upload'];
  clearUpload: OrderActions['clearUpload'];
  importAvatarPhoto: OrderActions['importAvatarPhoto'];
  submit: OrderActions['submit'];
  openResult: OrderActions['openResult'];
  refreshStatus: OrderActions['refreshStatus'];
  returnToForm: OrderActions['returnToForm'];
  retry: OrderActions['retry'];
};

/* ---------- чистые части ---------- */

/** Отказ сервера на создание заказа: ветки и тексты бывшего catch submit.
    drop — заявка снимается (повтор тем же ключом бессмыслен); stale true/false выставляет versionStale (первая ветка),
    без stale versionStale не трогается (409 *_unavailable). */
export type Refusal = { drop: true; stale?: boolean; text: string } | { drop: false; text: string };
export function refusal(failure: unknown, replay: boolean): Refusal {
  if (
    !replay &&
    failure instanceof ApiError &&
    ([400, 410, 422].includes(failure.status) ||
      (failure.status === 409 && ['price_changed', 'template_version_changed', 'workflow_configuration_changed'].includes(failure.code)))
  ) {
    const changed = failure.status === 409;
    return {
      drop: true,
      stale: changed,
      text:
        failure.code === 'circle_moderation_rejected'
          ? 'Текст отклонён проверкой. Измените текст.'
          : changed
            ? 'Цена или версия инструмента изменилась. Обновите форму и проверьте стоимость.'
            : 'Проверьте заполненные поля. Заказ не создан.',
    };
  }
  if (failure instanceof ApiError && failure.status === 409 && /_unavailable$/.test(failure.code))
    /* 409 *_unavailable (docs/03-api §ошибки, docs/59 §1): сервер окончательно отказал до создания заказа — повтор тем же
       ключом бессмыслен. Раньше заявка оставалась и кнопка звала «Продолжить проверку» (стенд: «Свой аватар»,
       409 avatar_creation_unavailable). */
    return {
      drop: true,
      text: ['cast_unavailable', 'avatar_unavailable', 'voice_unavailable', 'app_unavailable'].includes(failure.code)
        ? 'Выбранный персонаж, голос или шаблон стал недоступен. Выберите другой — заказ не создан.'
        : 'Создание сейчас недоступно. Заказ не создан, Circle не списаны.',
    };
  if (failure instanceof ApiError && failure.status === 409 && failure.code === 'upload_not_ready')
    /* Сервер отказал до создания: фото роли или поля не годится в команду (не загружено до конца, недоступно). Повтор тем же
       ключом даст тот же отказ — заявка снимается, а не висит «не подтверждён» (стенд 29.10: «Вы в кино» с персонажем STEOS). */
    return { drop: true, text: 'Фото ещё не готово или недоступно. Выберите его заново — заказ не создан, Circle не списаны.' };
  if (failure instanceof ApiError && failure.status === 409 && failure.code === 'active_order_limit')
    /* Сервер отказал до создания: идёт другой заказ. Ключ не израсходован — повтор тем же ключом безопасен,
       но человеку нужна причина, а не «не подтверждён». */
    return { drop: false, text: 'Предыдущая работа ещё создаётся. Когда она будет готова, отправьте заказ снова.' };
  /* Известный пробел (как раньше): 402 и прочие ответы считаются «не подтверждён» — заявка и ключ остаются. */
  return { drop: false, text: 'Заказ не подтверждён. Продолжение проверит прежний запрос с тем же ключом.' };
}

function derived(s: OrderSnapshot, ctx: OrderCtx): OrderDerived {
  const { form, inputs, quote, order, intent } = s;
  const submissionBlocked = !s.identity || s.identity.appId !== ctx.app.id || !!s.integrityError || s.versionStale;
  const locked = submissionBlocked || !!intent || s.busy;
  const quoteEndpoint = studioQuotePath((form as unknown as Record<string, unknown> | null)?.quote_endpoint);
  const quoteBody = (() => {
    if (!form || !quoteEndpoint) return null;
    try {
      return JSON.stringify({
        screen: form.screen,
        app_id: form.app_id,
        template_version: form.template_version,
        inputs: studioInputs(form.screen, publishedInputs(form, inputs), studioJsonKeys(form), studioJsonEnumKeys(form)),
      });
    } catch {
      return null;
    }
  })();
  /** Цена на кнопке и в заказе: расчёт сервера под состав, иначе оценка экрана, иначе цена формы */
  const quoted = !!form && !!quote && quote.key === quoteBody;
  const price = form
    ? quoted
      ? quote!.amount
      : (ctx.expectedPrice?.(form, inputs) ?? studioExpectedPrice(form, inputs))
    : 0;
  const orderId = order?.id ?? intent?.orderId;
  const terminal = order && ['done', 'failed', 'uncertain'].includes(order.status);
  return { submissionBlocked, locked, quoteEndpoint, quoteBody, price, quoted, orderId, terminal };
}

/** Новый снимок, если хоть один ключ patch отличается по Object.is; иначе тот же объект */
export function mergeSnapshot(current: OrderSnapshot, patch: Partial<OrderSnapshot>): OrderSnapshot {
  let next: OrderSnapshot | null = null;
  for (const key of Object.keys(patch) as Array<keyof OrderSnapshot>) {
    if (Object.is(current[key], patch[key])) continue;
    next ??= { ...current };
    (next as Record<keyof OrderSnapshot, unknown>)[key] = patch[key];
  }
  return next ?? current;
}

const resolve = <T>(value: Next<T>, current: T): T => (typeof value === 'function' ? (value as (current: T) => T)(current) : value);

/** Действия от явного состояния at: хук передаёт состояние своего рендера, сервис — текущее */
interface EntryActions {
  upload(at: OrderEntry, key: string, file: File | null): Promise<boolean | undefined>;
  uploadRaw(at: OrderEntry, policy: RawPolicy, file: File | null, pendingId?: string): Promise<RawUpload>;
  clearUpload(at: OrderEntry, key: string): void;
  importAvatarPhoto(at: OrderEntry, key: string): Promise<void>;
  submit(at: OrderEntry, options?: { cheap?: boolean; price?: number }): Promise<void>;
  refreshStatus(at: OrderEntry): Promise<void>;
  returnToForm(at: OrderEntry): Promise<true | undefined>;
  retry(at: OrderEntry): Promise<boolean>;
  openResult(at: OrderEntry, index: number): Promise<void>;
}
const entryActions = new WeakMap<OrderService, EntryActions>();

/* ---------- сервис ---------- */

/** Один ключ и снимок входов переживают неизвестный POST и обновление страницы. */
export function createOrderService(initialCtx: OrderCtx): OrderService {
  let snapshot: OrderSnapshot = {
    identity: null,
    balance: null,
    integrityError: '',
    versionStale: false,
    intent: null,
    form: null,
    inputs: {},
    order: null,
    busy: false,
    uploadingFile: false,
    error: '',
    revision: 0,
    uploadIds: {},
    voice: null,
    rights: false,
    result: null,
    quote: null,
  };
  const listeners = new Set<OrderListener>();
  /* Бывшие useRef: читаются живыми, а не из снимка */
  let pending: Intent | null = snapshot.intent,
    uploading = false;
  let controller: AbortController | null = null;
  let mounted = false;
  /** «Попробовать ещё раз» после сбоя: форма восстановлена — новый заказ с тем же входом (§1.4) */
  let retrying = false;
  /** Сбойный заказ был со скидкой («Сделать дешевле»): новый — тоже, если форма её публикует (тот же вход — та же цена) */
  let retryCheap = false;
  let outcome: SubmitOutcome | null = null;

  const get = () => snapshot;
  function set(patch: Partial<OrderSnapshot>) {
    const next = mergeSnapshot(snapshot, patch);
    if (next === snapshot) return;
    const changed: Partial<OrderSnapshot> = {};
    for (const key of Object.keys(patch) as Array<keyof OrderSnapshot>)
      if (!Object.is(snapshot[key], next[key])) (changed as Record<keyof OrderSnapshot, unknown>)[key] = next[key];
    snapshot = next;
    for (const listener of [...listeners]) listener(next, changed);
  }
  const setters: OrderService['setters'] = {
    inputs: value => set({ inputs: resolve(value, snapshot.inputs) }),
    revision: value => set({ revision: resolve(value, snapshot.revision) }),
    uploadIds: value => set({ uploadIds: resolve(value, snapshot.uploadIds) }),
    voice: value => set({ voice: resolve(value, snapshot.voice) }),
    rights: value => set({ rights: resolve(value, snapshot.rights) }),
  };
  const live = (): OrderEntry => ({ s: snapshot, d: derived(snapshot, svc.ctx), ctx: svc.ctx });

  function save(next: Intent) {
    const copy = JSON.parse(JSON.stringify(next)) as Intent;
    sessionStorage.setItem(storageKey(copy.owner, copy.body.app_id), JSON.stringify(copy));
    pending = copy;
    set({ intent: copy });
  }
  async function verifyOwner({ s }: OrderEntry, request: AbortController) {
    const owner = await endpoints.me();
    if (request.signal.aborted) return false;
    if (!s.identity || owner.user.id !== s.identity.owner || (pending && pending.owner !== owner.user.id)) {
      set({ integrityError: 'Владелец сессии изменился. Вернитесь в студию после обновления страницы.' });
      return false;
    }
    return true;
  }

  async function upload(at: OrderEntry, key: string, file: File | null) {
    const { form, rights, uploadIds } = at.s;
    if (!form || uploading || at.d.locked || !rights) return false;
    const policy = studioFields(form).find(field => field.key === key)?.upload;
    if (!policy || (!file && !uploadIds[key])) return false;
    uploading = true;
    set({ busy: true });
    set({ uploadingFile: true });
    set({ error: '' });
    const request = new AbortController();
    controller = request;
    try {
      const slot = uploadIds[key]
        ? await endpoints.waitForUpload(uploadIds[key], request.signal)
        : await endpoints.uploadFile(
            file!,
            {
              purpose: uploadPurpose(policy),
              kind: policy.kind,
              origin: 'file',
              ...(uploadPurpose(policy) === 'reference' && (policy.use ?? referenceUse(form)) ? { use: policy.use ?? referenceUse(form) } : {}),
            },
            uuid(),
            request.signal,
          );
      if (!request.signal.aborted) {
        setters.inputs(current => ({ ...current, [key]: slot.id }));
        setters.uploadIds(current => ({ ...current, [key]: slot.id }));
        return true;
      }
      return false;
    } catch (failure) {
      if (!request.signal.aborted) {
        if (failure instanceof UploadTransportError) setters.uploadIds(current => ({ ...current, [key]: failure.uploadId }));
        set({ error: uploadFailureText(failure) });
      }
      /* Известный пробел (как раньше): на этой ветке upload возвращает undefined, а не false. */
    } finally {
      uploading = false;
      if (!request.signal.aborted) set({ busy: false });
      set({ uploadingFile: false });
    }
  }
  /** Загрузка для вложенного ключа контракта экрана (objects.*, song_upload_id и т. п., docs/59 §1): тот же POST /app/uploads,
      UUID возвращается экрану, а не пишется в inputs. pendingId — проверить неподтверждённую загрузку без нового файла. */
  async function uploadRaw(at: OrderEntry, policy: RawPolicy, file: File | null, pendingId?: string): Promise<RawUpload> {
    if (!at.s.form || uploading || at.d.locked || !at.s.rights || (!file && !pendingId)) return { error: true, pendingId };
    uploading = true;
    set({ uploadingFile: true });
    set({ busy: true });
    set({ error: '' });
    const request = new AbortController();
    controller = request;
    try {
      const slot = pendingId
        ? await endpoints.waitForUpload(pendingId, request.signal)
        : await endpoints.uploadFile(
            file!,
            {
              purpose: (policy.purpose ?? 'order_input') as 'order_input',
              kind: policy.kind,
              origin: 'file',
              ...(policy.purpose === 'reference' && policy.use ? { use: policy.use as 'style' } : {}),
            },
            uuid(),
            request.signal,
          );
      if (slot.status !== 'ready') throw new Error('upload_not_ready');
      return { id: slot.id };
    } catch (failure) {
      if (!request.signal.aborted) set({ error: uploadFailureText(failure) });
      const reason = failure instanceof UploadRejectedError ? failure.upload.reject_reason : null;
      return { error: true, pendingId: failure instanceof UploadTransportError ? failure.uploadId : pendingId, ...(reason ? { reason } : {}) };
    } finally {
      uploading = false;
      if (!request.signal.aborted) set({ busy: false });
      set({ uploadingFile: false });
    }
  }
  function clearUpload(at: OrderEntry, key: string) {
    if (at.d.locked) return;
    setters.inputs(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setters.uploadIds(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }
  async function importAvatarPhoto(at: OrderEntry, key: string) {
    const { avatar } = at.ctx;
    if (!avatar?.image_url || at.d.locked || !at.s.rights || uploading) return;
    const request = new AbortController();
    controller = request;
    set({ uploadingFile: true });
    uploading = true;
    set({ busy: true });
    set({ error: '' });
    try {
      // Фото читаем через авторизованный Backend: настройки CORS хранилища для этого не нужны.
      const blob = await endpoints.people.avatarPhoto(avatar.id, request.signal);
      if (request.signal.aborted) return;
      if (!['image/jpeg', 'image/png'].includes(blob.type)) throw new Error('avatar_photo_invalid');
      uploading = false;
      const extension = blob.type === 'image/jpeg' ? 'jpg' : 'png';
      /* Загрузка стартует с того же входного состояния (busy в нём ещё false), как вложенный вызов бывшего хука. */
      await upload(at, key, new File([blob], `variant-${avatar.id}.${extension}`, { type: blob.type }));
    } catch {
      if (!request.signal.aborted) set({ error: 'Не удалось загрузить фото варианта. Обновите профиль.' });
    } finally {
      uploading = false;
      if (!request.signal.aborted) set({ busy: false });
      set({ uploadingFile: false });
    }
  }
  /** options.cheap — «Сделать дешевле» из листа «Не хватает Circle»: только если форма публикует скидку (form.cheap). */
  async function submit(at: OrderEntry, options: { cheap?: boolean; price?: number } = {}) {
    outcome = null;
    /* После создания заказа итог остаётся created, даже если дальше что-то бросило */
    const end = (value: SubmitOutcome) => {
      if (outcome !== 'created') outcome = value;
    };
    const { s, d, ctx } = at;
    const { form, inputs, identity } = s;
    if ((!form && !pending) || uploading || d.orderId || d.submissionBlocked) {
      end('skipped');
      return;
    }
    uploading = true;
    set({ busy: true });
    set({ error: '' });
    const request = new AbortController();
    controller = request;
    const replay = !!pending;
    const cheapOffer = options.cheap && form ? studioCheapOffer(form) : null;
    try {
      if (!(await verifyOwner(at, request))) {
        end(request.signal.aborted ? 'skipped' : 'blocked');
        return;
      }
      const json = form ? studioJsonKeys(form) : [];
      const own = options.price ?? d.price;
      const value: Intent = pending ?? {
        owner: identity!.owner,
        key: uuid(),
        body: {
          screen: form!.screen,
          app_id: form!.app_id,
          template_version: form!.template_version,
          expected_price: cheapOffer ? cheapOffer.price : own,
          inputs: studioInputs(form!.screen, publishedInputs(form!, inputs), json, studioJsonEnumKeys(form)),
          ...(cheapOffer ? { cheap: true } : {}),
        },
        ...(json.length ? { json } : {}),
      };
      try {
        save(value);
      } catch {
        set({ integrityError: 'Не удалось сохранить заявку. Отправка не выполнена.' });
        end('blocked');
        return;
      }
      const created = await endpoints.speech.create(value.body, value.key, request.signal);
      clearHistoryRepeat();
      save({ ...value, orderId: created.id });
      end('created');
      if (['who', 'circle', 'photo', 'video', 'dance', 'swap', 'reels', 'mcard', 'post', 'infog', 'shoot', 'pshoot', 'cover'].includes(value.body.screen)) {
        publishStudioOrder({
          owner: value.owner,
          orderId: created.id,
          appId: value.body.app_id,
          status: created.status,
          kind: ['reels', 'mcard', 'post', 'infog', 'shoot', 'pshoot', 'cover'].includes(value.body.screen)
            ? (value.body.screen as 'reels' | 'mcard' | 'post' | 'infog' | 'shoot' | 'pshoot' | 'cover')
            : ['dance', 'swap'].includes(value.body.screen)
            ? value.body.inputs.preset === 'swap' || value.body.screen === 'swap' || form?.constraints?.motion?.preset === 'swap'
              ? 'swap'
              : 'dance'
            : value.body.screen === 'photo'
              ? 'photo'
              : value.body.screen === 'video'
                ? 'video'
                : value.body.inputs.format === 'circle'
                  ? 'circle'
                  : 'lipsync',
          shown: false,
        });
      }
      if (!request.signal.aborted) set({ order: created });
    } catch (failure) {
      if (!request.signal.aborted) {
        const answer = refusal(failure, replay);
        if (answer.drop) {
          try {
            sessionStorage.removeItem(storageKey(identity!.owner, ctx.app.id));
            pending = null;
            set({ intent: null });
            if (answer.stale !== undefined) set({ versionStale: answer.stale });
            set({ error: answer.text });
            end('dropped');
          } catch {
            set({ integrityError: 'Не удалось обновить сохранённую заявку. Новая генерация заблокирована.' });
            end('blocked');
          }
        } else {
          set({ error: answer.text });
          end('kept');
        }
      } else end('skipped');
    } finally {
      uploading = false;
      if (!request.signal.aborted) set({ busy: false });
    }
  }
  async function refreshStatus(at: OrderEntry) {
    const { orderId, submissionBlocked } = at.d;
    if (!orderId || uploading || submissionBlocked) return;
    uploading = true;
    set({ busy: true });
    const request = new AbortController();
    controller = request;
    try {
      if (!(await verifyOwner(at, request))) return;
      const fresh = await endpoints.speech.get(orderId, request.signal);
      if (!request.signal.aborted) {
        set({ order: fresh });
        set({ error: '' });
      }
    } catch {
      if (!request.signal.aborted) set({ error: 'Статус пока не подтверждён. Проверьте этот же заказ позже.' });
    } finally {
      uploading = false;
      if (!request.signal.aborted) set({ busy: false });
    }
  }
  async function returnToForm(at: OrderEntry) {
    const { s, d, ctx } = at;
    const { orderId, submissionBlocked } = d;
    if (!orderId || uploading || submissionBlocked) return;
    uploading = true;
    set({ busy: true });
    const request = new AbortController();
    controller = request;
    try {
      if (!(await verifyOwner(at, request))) return;
      const fresh = await endpoints.speech.get(orderId, request.signal);
      if (request.signal.aborted) return;
      if (!['done', 'failed'].includes(fresh.status)) {
        set({ order: fresh });
        set({ error: 'Прежний заказ ещё не завершён. Сначала проверьте его результат.' });
        return;
      }
      const original = pending;
      if (!original || original.owner !== s.identity!.owner || original.body.app_id !== ctx.app.id) return;
      const nextForm = await studioApi.form(ctx.app.id, request.signal);
      if (nextForm.app_id !== ctx.app.id || nextForm.screen !== original.body.screen) throw new Error('form_changed');
      const prefill = await studioPrefill(nextForm, original.body.inputs, ctx.avatar, request.signal);
      if (request.signal.aborted || !(await verifyOwner(at, request))) return;
      // История и финансовая квитанция остаются на сервере; новый ключ появится только после следующего «Создать».
      sessionStorage.removeItem(storageKey(s.identity!.owner, ctx.app.id));
      pending = null;
      set({ intent: null });
      set({ order: null });
      set({ result: null });
      set({ form: nextForm });
      set({ inputs: prefill.inputs });
      set({ uploadIds: prefill.uploadIds });
      set({ voice: prefill.voice });
      set({ versionStale: false });
      set({ error: prefill.unavailableFile ? 'Некоторые файлы повтора больше недоступны. Загрузите их заново.' : '' });
      return true as const;
    } catch {
      if (!request.signal.aborted) set({ error: 'Не удалось подтвердить завершение заказа. Новая генерация не начата.' });
    } finally {
      uploading = false;
      if (!request.signal.aborted) set({ busy: false });
    }
  }
  /** «Попробовать ещё раз» (§1.4, §16): новый заказ с тем же входом; Circle сбойного заказа сервер уже вернул. */
  async function retry(at: OrderEntry) {
    if (at.s.order?.status !== 'failed') return false;
    const cheap = !!pending?.body.cheap;
    if (!(await returnToForm(at))) return false;
    retrying = true;
    retryCheap = cheap;
    return true;
  }
  async function openResult(at: OrderEntry, index: number) {
    const { order } = at.s;
    if (!order?.result?.available || uploading) return;
    const asset = order.result.assets.find(item => item.index === index);
    if (!asset) return;
    uploading = true;
    set({ busy: true });
    set({ error: '' });
    const request = new AbortController();
    controller = request;
    let timedOut = false;
    let rejectCancelled!: (reason: DOMException) => void;
    const cancelled = new Promise<never>((_, reject) => {
      rejectCancelled = reject;
    });
    const onAbort = () => rejectCancelled(new DOMException('download_cancelled', 'AbortError'));
    request.signal.addEventListener('abort', onAbort, { once: true });
    const timeout = setTimeout(() => {
      timedOut = true;
      request.abort();
    }, 30000);
    try {
      // Дедлайн ограничивает и ожидание транспорта, и чтение тела. Повтор читает тот же заказ, не создавая новый.
      const blob = await Promise.race([endpoints.speech.result(order, index, request.signal), cancelled]);
      const text = asset.mime.startsWith('text/') ? await Promise.race([blob.text(), cancelled]) : undefined;
      if (mounted && !request.signal.aborted)
        set({
          result: {
            url: URL.createObjectURL(blob),
            mime: asset.mime,
            name: asset.file_name,
            ...(text !== undefined ? { text } : {}),
          },
        });
    } catch {
      if (mounted && (!request.signal.aborted || timedOut)) set({ error: 'Не удалось открыть результат. Попробуйте снова.' });
    } finally {
      clearTimeout(timeout);
      request.signal.removeEventListener('abort', onAbort);
      uploading = false;
      if (controller === request) controller = null;
      if (mounted && (!request.signal.aborted || timedOut)) set({ busy: false });
    }
  }
  const bound: EntryActions = { upload, uploadRaw, clearUpload, importAvatarPhoto, submit, refreshStatus, returnToForm, retry, openResult };

  const reactions: OrderService['reactions'] = {
    owner(appId) {
      let active = true;
      /* сбой сети или 5xx шлюза — ещё до трёх попыток с паузой: один 502 навсегда гасил кнопку «Создать»
         («Не удалось проверить владельца»), пока форму не откроешь заново. Ответ 4xx — сразу ошибка */
      const me = async () => {
        for (let attempt = 0; ; attempt++) {
          try {
            return await endpoints.me();
          } catch (failure) {
            if (!active || attempt >= 3 || (failure instanceof ApiError && failure.status > 0 && failure.status < 500)) throw failure;
            await new Promise(resolve => setTimeout(resolve, 700 * (attempt + 1)));
          }
        }
      };
      void me()
        .then(async me => {
          if (!active) return;
          try {
            if (!Number.isSafeInteger(me.user.id) || me.user.id <= 0) throw new Error('studio_owner_invalid');
            let restored = restore(me.user.id, appId);
            const repeat = readHistoryRepeat('studio', me.user.id);
            if (repeat?.app_id === appId && restored?.orderId) {
              const previous = await endpoints.speech.get(restored.orderId);
              if (!active) return;
              // Явный «Повторить» открывает новую форму только после подтверждённого terminal старого intent.
              if (['done', 'failed'].includes(previous.status)) {
                const currentOwner = await endpoints.me();
                if (!active) return;
                if (currentOwner.user.id !== me.user.id) throw new Error('owner_changed');
                const saved = restore(me.user.id, appId);
                if (saved?.key !== restored.key) throw new Error('intent_changed');
                sessionStorage.removeItem(storageKey(me.user.id, appId));
                document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id: restored.orderId } }));
                restored = null;
              }
            }
            pending = restored;
            set({ intent: restored });
            set({ identity: { owner: me.user.id, appId } });
            const circle = (me as { balance?: { circle?: unknown } }).balance?.circle;
            set({ balance: typeof circle === 'number' && Number.isFinite(circle) ? circle : null });
            set({ integrityError: '' });
          } catch {
            set({ integrityError: 'Не удалось восстановить сохранённую заявку. Новая генерация заблокирована.' });
          }
        })
        .catch(() => {
          if (active) set({ integrityError: 'Не удалось проверить владельца. Новая генерация заблокирована.' });
        });
      return () => {
        active = false;
      };
    },
    /* revision — только зависимость эффекта: новая ревизия перечитывает форму */
    form(appId, avatar, _revision, initialInputs) {
      const request = new AbortController();
      void studioApi
        .form(appId, request.signal)
        .then(async value => {
          if (request.signal.aborted) return;
          const prefill = await studioPrefill(value, initialInputs, avatar, request.signal);
          if (request.signal.aborted) return;
          set({ form: value });
          set({ versionStale: false });
          set({ inputs: prefill.inputs });
          set({ uploadIds: prefill.uploadIds });
          set({ voice: prefill.voice });
          set({ error: prefill.unavailableFile ? 'Некоторые файлы повтора больше недоступны. Загрузите их заново.' : '' });
        })
        .catch(failure => {
          if (!request.signal.aborted)
            set({
              error:
                failure instanceof ApiError && failure.code === 'workflow_configuration_changed'
                  ? 'Инструмент обновляется. Обновите форму позже.'
                  : 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
            });
        });
      return () => request.abort();
    },
    quote(quoteEndpoint, quoteBody) {
      if (!quoteEndpoint || !quoteBody) return;
      const request = new AbortController();
      const timer = window.setTimeout(() => {
        void api
          .request<{ amount?: unknown }>(quoteEndpoint, { method: 'POST', body: JSON.parse(quoteBody), signal: request.signal })
          .then(answer => {
            if (!request.signal.aborted && typeof answer?.amount === 'number' && Number.isSafeInteger(answer.amount) && answer.amount >= 0)
              set({ quote: { key: quoteBody, amount: answer.amount } });
          })
          .catch(() => {
            /* Без расчёта остаётся цена формы; сервер всё равно сверит expected_price. */
          });
      }, 300);
      return () => {
        request.abort();
        clearTimeout(timer);
      };
    },
    poll(orderId, terminal) {
      if (!orderId || terminal) return;
      const request = new AbortController();
      let timer: number | undefined;
      const poll = async () => {
        try {
          const fresh = await endpoints.speech.get(orderId, request.signal);
          if (!request.signal.aborted) {
            set({ order: fresh });
            set({ error: '' });
            if (['queued', 'running'].includes(fresh.status)) timer = window.setTimeout(() => void poll(), 3000);
          }
        } catch {
          if (!request.signal.aborted) {
            set({ error: 'Статус пока не подтверждён. Проверяем прежний заказ.' });
            timer = window.setTimeout(() => void poll(), 5000);
          }
        }
      };
      void poll();
      return () => {
        request.abort();
        clearTimeout(timer);
      };
    },
    mount() {
      mounted = true;
      return () => {
        mounted = false;
        /* Известный пробел (как раньше): прерывается только последний запрос действия. */
        controller?.abort();
      };
    },
    revoke(result) {
      return () => {
        if (result) URL.revokeObjectURL(result.url);
      };
    },
    retryTick(at = live()) {
      if (!retrying || !at.s.form || at.d.orderId || pending || at.s.busy) return;
      retrying = false;
      void (retryCheap ? submit(at, { cheap: true }) : submit(at));
    },
  };

  const svc: OrderService = {
    ctx: initialCtx,
    get,
    derive: (s = snapshot, ctx = svc.ctx) => derived(s, ctx),
    subscribe(listener, since) {
      listeners.add(listener);
      if (since !== undefined && since !== snapshot) listener(snapshot, snapshot);
      return () => {
        listeners.delete(listener);
      };
    },
    get outcome() {
      return outcome;
    },
    setters,
    actions: {
      upload: (key, file) => upload(live(), key, file),
      uploadRaw: (policy, file, pendingId) => uploadRaw(live(), policy, file, pendingId),
      clearUpload: key => clearUpload(live(), key),
      importAvatarPhoto: key => importAvatarPhoto(live(), key),
      submit: options => submit(live(), options),
      refreshStatus: () => refreshStatus(live()),
      returnToForm: () => returnToForm(live()),
      retry: () => retry(live()),
      openResult: index => openResult(live(), index),
    },
    reactions,
  };
  entryActions.set(svc, bound);
  return svc;
}

/** Пропсы зафиксированного рендера — для действий движка (svc.actions, svc.derive() без аргументов) */
export function bindOrderCtx(svc: OrderService, ctx: OrderCtx) {
  svc.ctx = ctx;
}

/** Вид хука: новый объект на каждый рендер (на это опираются зависимости [state] экранов); сеттеры стабильны,
    действия — новые обёртки на каждый рендер и работают на состоянии и пропсах этого рендера, как замыкания бывшего хука. */
export function hookView(svc: OrderService, s: OrderSnapshot, d: OrderDerived, ctx: OrderCtx = svc.ctx): StudioFormView {
  const run = entryActions.get(svc)!;
  const at: OrderEntry = { s, d, ctx };
  return {
    ownerId: s.identity?.owner,
    balance: s.balance,
    price: d.price,
    uploadRaw: (policy, file, pendingId) => run.uploadRaw(at, policy, file, pendingId),
    form: s.form,
    inputs: s.inputs,
    setInputs: svc.setters.inputs,
    intent: s.intent,
    order: s.order,
    orderId: d.orderId,
    busy: s.busy,
    uploadingFile: s.busy && s.uploadingFile,
    error: s.integrityError || s.error,
    submissionBlocked: d.submissionBlocked,
    setRevision: svc.setters.revision,
    locked: d.locked,
    rights: s.rights,
    setRights: svc.setters.rights,
    uploadIds: s.uploadIds,
    setUploadIds: svc.setters.uploadIds,
    voice: s.voice,
    setVoice: svc.setters.voice,
    result: s.result,
    upload: (key, file) => run.upload(at, key, file),
    clearUpload: key => run.clearUpload(at, key),
    importAvatarPhoto: key => run.importAvatarPhoto(at, key),
    submit: options => run.submit(at, options),
    openResult: index => run.openResult(at, index),
    refreshStatus: () => run.refreshStatus(at),
    returnToForm: () => run.returnToForm(at),
    retry: () => run.retry(at),
  };
}
